import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Bell, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  getCurrentSubscription,
} from "@/lib/pushSubscribe";

const DISMISS_KEY = "recoverme:push-prompt-dismissed";
const SHOW_DELAY_MS = 90_000; // Wait 90s of session activity before prompting

export default function EnablePushPrompt() {
  const { toast } = useToast();
  const [shouldShow, setShouldShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function maybeShow() {
      if (!isPushSupported()) return;
      if (localStorage.getItem(DISMISS_KEY)) return;

      const perm = getNotificationPermission();
      if (perm !== "default") return; // Already granted or denied — no prompt.

      const existing = await getCurrentSubscription();
      if (existing) return; // Already subscribed.

      const t = setTimeout(() => {
        if (!cancelled) setShouldShow(true);
      }, SHOW_DELAY_MS);
      return () => clearTimeout(t);
    }

    const cleanup = maybeShow();
    return () => {
      cancelled = true;
      Promise.resolve(cleanup).then((fn) => fn && fn());
    };
  }, []);

  if (!shouldShow) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShouldShow(false);
  };

  const enable = async () => {
    setBusy(true);
    const sub = await subscribeToPush();
    setBusy(false);
    if (sub) {
      toast({
        title: "Notifications enabled",
        description: "You'll be alerted to connection requests and SOS messages.",
      });
      localStorage.setItem(DISMISS_KEY, "1");
      setShouldShow(false);
    } else {
      toast({
        title: "Could not enable notifications",
        description: "You can try again from Settings → Notifications.",
        variant: "destructive",
      });
    }
  };

  return (
    <div
      className="fixed z-40 max-w-sm"
      style={{
        right: "max(1rem, env(safe-area-inset-right))",
        bottom: "max(7.5rem, calc(env(safe-area-inset-bottom) + 6.5rem))",
      }}
      data-testid="prompt-enable-push"
    >
      <Card className="p-4 shadow-lg space-y-3">
        <div className="flex items-start gap-3">
          <Bell className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Stay connected</p>
            <p className="text-xs text-muted-foreground mt-1">
              Get notified about new connection requests and SOS messages from your support network. Notifications never reveal who sent them.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={dismiss}
            data-testid="button-dismiss-push-prompt"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={dismiss}
            data-testid="button-not-now-push"
          >
            Not now
          </Button>
          <Button
            size="sm"
            onClick={enable}
            disabled={busy}
            data-testid="button-enable-push"
          >
            {busy ? "Enabling…" : "Enable"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
